// Curated collections — hand-picked shelves that cut across sections.
//
// A collection is not a section and not a level. A section is where a
// video lives (Drills & Games, Workout Examples, Insights). A level is
// who it's hard enough for. A collection is a shelf somebody curated by
// hand: "these came out of our pro sessions", and that can be true of a
// full workout, a single drill, an insight video or a masterclass at
// once.
//
// Kebab-case ids land in `drills.collections text[]`. Labels are
// display-only. Backed by supabase/migrations/2026-09-19_drills_collections.sql.
//
// Nothing in the AI enrichment pipeline touches this column, which is
// the whole reason it exists — see the migration for the detail.

export const COLLECTIONS = [
  {
    id: "pro",
    label: "Pro Development",
    // The chip that opens the collection says less than the page does.
    // It sits in a row of filter chips, so it has to read at a glance;
    // the curiosity goes in the hover, where there is room for a
    // question rather than a description.
    chipLabel: "Pro",
    hint: "Curious how we train professional players?",
  },
  {
    id: "article",
    label: "Article clips",
    // A holding pen, and the only collection with no page behind it.
    //
    // Clips that exist to be embedded in an article or a masterclass and
    // listed NOWHERE else: the nine numbered fragments of a zoom-action
    // progression, a podcast pull, a player's specific constraint. Each one
    // is the article's argument rather than a drill a coach would browse to,
    // and a shelf full of "ZOOM5.1" is noise on any page it lands on.
    //
    // The mechanism is the one that already existed. Adding anything to a
    // collection sets `collection_only`, which every coach-facing drill query
    // drops (see NOT_HELD below, and heldGate.test.js, which fails the build
    // if a new surface forgets). The difference is that Pro Development then
    // shows what it holds on its own page, and this has no page: every coach
    // surface names PRO_COLLECTION, so nothing renders this one.
    //
    // Deliberately no chipLabel and no hint. Those are page furniture, and
    // collections.test.js fails if this grows them without somebody also
    // building the page they imply.
    noPage: true,
  },
];

export const COLLECTION_IDS = COLLECTIONS.map(c => c.id);

export const COLLECTION_LABEL = Object.fromEntries(
  COLLECTIONS.map(c => [c.id, c.label])
);

export const COLLECTION_BY_ID = Object.fromEntries(
  COLLECTIONS.map(c => [c.id, c])
);

// Chip labels on the collection page. These are deliberately shorter
// and blunter than SECTION_LABEL: on this page a coach is asking "whole
// workout or single drill?", not "which library did this come from?".
export const PRO_KIND_LABEL = {
  workouts: "Full Workouts",
  pd:       "Drills",
  team:     "Team Drills",
  insights: "Insights",
  master:   "Masterclasses",
};

// The collection that gets its own page + entry chip today. When a
// second one earns a page, this becomes a lookup rather than a constant.
export const PRO_COLLECTION = "pro";

// ── Longer videos ───────────────────────────────────────────────────
//
// The Pro Development page splits into two shelves: the long stuff on
// top, then everything else. A coach arriving here wants to see how a
// whole session runs before they want a single rep, and a full workout
// buried among sixty drills never gets found.
//
// Eight minutes is the line. It is a proxy rather than a definition —
// what it is really separating is "watch this through" from "grab this
// one thing" — so expect the odd long drill to land on the top shelf.
// That is the cost of a rule a coach can predict.
export const LONG_VIDEO_SECONDS = 8 * 60;

export const isLongVideo = (item) =>
  !!item && Number(item.durationSec || 0) >= LONG_VIDEO_SECONDS;

// ── Entry transition ────────────────────────────────────────────────
//
// Clicking the Pro Development chip on Drills & Games is a doorway, not a
// filter, so it gets a transition that says "you went somewhere" rather
// than "the list changed". Five are implemented as CSS keyframe pairs
// in dashboard.css (search PRO ENTRY TRANSITIONS); switch by changing
// this one value.
//
// All of it plays on ARRIVAL. The route changes the instant you click
// and the overlay lifts off the page you have landed on, so none of
// this is time anyone spends waiting. Only "lights" has been rebuilt
// that way so far - the other four still read as the older
// fade-in-then-cut and want the same treatment before they are used.
//
//   "lights"   Court lights coming up. The page arrives covered, a warm
//              pool opens outward as the cover clears, and the grid
//              lifts the last few pixels into place. The most cinematic.
//   "sweep"    A gold band wipes across the screen and the page is
//              behind it. Fast, graphic, reads as a scene change.
//   "doorway"  The drill grid pushes away from you and the collection
//              eases back from slightly overscale. Quiet, physical.
//   "hardcut"  A single frame of black, then the page. Brutal, and
//              it never gets annoying on the fiftieth visit.
//   "riser"    The page slides up over the grid like a panel, with the
//              grid dimming behind it. The most app-native.
//
export const PRO_TRANSITION = "lights";

// How long the arrival overlay stays mounted. The route changes on the
// click; this is the cover lifting off the page you are already on, so
// it is never time anybody waits. Keep in step with the keyframe
// durations in dashboard.css, and round up rather than down: unmounting
// mid-animation is a visible snap.
export const PRO_TRANSITION_MS = {
  lights: 400,
  sweep: 340,
  doorway: 260,
  hardcut: 140,
  riser: 220,
};

// ── Held ────────────────────────────────────────────────────────────
//
// `collections` says which shelves a drill is ON. `collection_only` says
// it is ONLY on them: held back from the section libraries until someone
// releases it. Two independent columns, because a drill can be on the Pro
// shelf and in Drills & Games at the same time, and that is a real thing
// to want.
//
// This filter was a hand-typed string living in useDrills.js, and in
// nothing else. So "held" meant held in the Drills & Games grid, and
// nowhere else: a held drill still turned up in this week's drops on the
// home screen, in the practice planner's drill search, and in the
// community drill picker. A coach could not browse to it and could
// absolutely still find it, which is not what the person holding it
// thought they were buying.
//
// So it lives here now, as one export every drill query shares. If a new
// surface lists drills for a coach to browse, it uses this. heldGate.test.js
// walks the source and fails on a query that does neither that nor appear
// in its exemption list with a reason written next to it.
export const NOT_HELD = "collection_only.is.false,collection_only.is.null";

/**
 * Drop held drills from a supabase query.
 *
 * `or` rather than `eq(false)` because the column postdates most of the
 * table: rows written before the migration are null, and `eq(false)` would
 * silently hide the entire back catalogue.
 */
export const excludeHeld = (q) => q.or(NOT_HELD);
